import type { Editor } from '@tiptap/react';
import type { Node as PmNode } from '@tiptap/pm/model';
import { DOMSerializer } from '@tiptap/pm/model';
import type { PendingDraftChange } from '../store/lexCounselStore';

export interface ParagraphMatch {
  paraId: string;
  pos: number;
  node: PmNode;
  index: number;
}

function newParaId(): string {
  return `para-${crypto.randomUUID()}`;
}

export function listLegalParagraphs(doc: PmNode): ParagraphMatch[] {
  const matches: ParagraphMatch[] = [];
  let index = 0;
  doc.descendants((node, pos) => {
    if (node.type.name !== 'paragraph') {
      return true;
    }
    const paraId = (node.attrs.paraId as string | null) ?? null;
    if (!paraId) return true;
    index += 1;
    matches.push({ paraId, pos, node, index });
    return false;
  });
  return matches;
}

export function ensureParagraphIds(editor: Editor): void {
  const { state } = editor;
  const tr = state.tr;
  let changed = false;

  state.doc.descendants((node, pos) => {
    if (node.type.name !== 'paragraph') {
      return true;
    }
    if (node.attrs.paraId) return true;
    tr.setNodeMarkup(pos, undefined, {
      ...node.attrs,
      paraId: newParaId(),
    });
    changed = true;
    return false;
  });

  if (changed) {
    editor.view.dispatch(tr);
  }
}

export function findParagraphByIdInDoc(doc: PmNode, paraId: string): ParagraphMatch | null {
  let found: ParagraphMatch | null = null;
  let index = 0;
  doc.descendants((node, pos) => {
    if (node.type.name !== 'paragraph') {
      return true;
    }
    const nodeParaId = (node.attrs.paraId as string | null) ?? null;
    if (!nodeParaId) return true;
    index += 1;
    if (nodeParaId === paraId) {
      found = { paraId: nodeParaId, pos, node, index };
      return false;
    }
    return false;
  });
  return found;
}

export function findParagraphById(editor: Editor, paraId: string): ParagraphMatch | null {
  return findParagraphByIdInDoc(editor.state.doc, paraId);
}

export function getParagraphHtml(editor: Editor, paraId: string): string | null {
  const match = findParagraphById(editor, paraId);
  if (!match) return null;
  const serializer = DOMSerializer.fromSchema(editor.schema);
  const fragment = serializer.serializeNode(match.node);
  const div = document.createElement('div');
  div.appendChild(fragment);
  return div.innerHTML;
}

export function getSelectionHtml(editor: Editor, from: number, to: number): string {
  const { doc } = editor.state;
  const slice = doc.slice(from, to);
  const serializer = DOMSerializer.fromSchema(editor.schema);
  const fragment = serializer.serializeFragment(slice.content);
  const div = document.createElement('div');
  div.appendChild(fragment);
  return div.innerHTML;
}

export function normalizeSelectionReplacementHtml(html: string): string {
  const trimmed = html.trim();
  if (!trimmed) return '';
  const singleParagraph = trimmed.match(/^<p(?:\s[^>]*)?>([\s\S]*)<\/p>$/i);
  if (singleParagraph) return singleParagraph[1].trim();
  return trimmed;
}

export function scrollToPendingDraftChange(
  editor: Editor,
  change: PendingDraftChange,
): boolean {
  if (change.scope === 'paragraph' && change.paragraphId) {
    const match = findParagraphById(editor, change.paragraphId);
    if (!match) return false;
    const dom = editor.view.nodeDOM(match.pos);
    if (dom instanceof HTMLElement) {
      dom.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return true;
    }
    return false;
  }

  if (change.scope === 'selection' && change.selectionFrom != null) {
    editor
      .chain()
      .setTextSelection(change.selectionFrom)
      .scrollIntoView()
      .run();
    return true;
  }

  if (change.scope === 'document') {
    editor.chain().focus('start').scrollIntoView().run();
    return true;
  }

  return false;
}

export function replaceSelectionHtml(
  editor: Editor,
  from: number,
  to: number,
  html: string,
): boolean {
  // deleteRange + insertContentAt supports single- and multi-paragraph ranges.
  const { doc } = editor.state;
  const docSize = doc.content.size;
  if (from < 0 || to > docSize || from > to) return false;

  editor
    .chain()
    .focus()
    .deleteRange({ from, to })
    .insertContentAt(from, html, {
      parseOptions: { preserveWhitespace: 'full' },
    })
    .run();

  ensureParagraphIds(editor);
  return true;
}

export function replaceParagraphHtml(editor: Editor, paraId: string, html: string): boolean {
  const match = findParagraphById(editor, paraId);
  if (!match) return false;

  const from = match.pos;
  const to = match.pos + match.node.nodeSize;
  const htmlWithId = html.includes('data-para-id')
    ? html
    : html.replace(/<p\b/i, `<p data-para-id="${paraId}"`);

  editor
    .chain()
    .focus()
    .deleteRange({ from, to })
    .insertContentAt(from, htmlWithId, {
      parseOptions: { preserveWhitespace: 'full' },
    })
    .run();

  ensureParagraphIds(editor);
  return true;
}

const PARA_REF_RE =
  /\b(?:para(?:graph)?|clause|section|point)\s*(?:no\.?|number)?\s*(\d+)\b/i;

export function resolveParagraphReference(
  editor: Editor,
  text: string,
): { paraId: string; index: number } | null {
  const match = text.match(PARA_REF_RE);
  if (!match) return null;
  const targetIndex = Number(match[1]);
  if (!Number.isFinite(targetIndex) || targetIndex < 1) return null;

  const paragraphs = listLegalParagraphs(editor.state.doc);
  const target = paragraphs[targetIndex - 1];
  if (!target) return null;
  return { paraId: target.paraId, index: target.index };
}

export function paragraphAtPos(editor: Editor, pos: number): ParagraphMatch | null {
  const $pos = editor.state.doc.resolve(pos);
  for (let depth = $pos.depth; depth > 0; depth -= 1) {
    const node = $pos.node(depth);
    if (node.type.name !== 'paragraph') {
      continue;
    }
    const paraId = (node.attrs.paraId as string | null) ?? null;
    if (!paraId) return null;
    const nodePos = $pos.before(depth);
    const paragraphs = listLegalParagraphs(editor.state.doc);
    const found = paragraphs.find((p) => p.paraId === paraId);
    return found ?? { paraId, pos: nodePos, node, index: paragraphs.length };
  }
  return null;
}
